import {
  DOCS_URLS,
  NormalizedUser,
  Tenant,
  UserRole,
  assertNever,
  getCurrentToken,
  useAuthFetch,
  useUser,
} from "@p0security/frontend-core";
import {
  DB,
  useFirestoreCollection,
  useFirestoreDoc,
} from "@p0security/frontend-core/react";
import { AppPaths } from "@p0security/shared/routes/constants";
import { Directory, IdpGroup } from "@p0security/shared/types/policy/types";
import type { OrgAuth } from "@p0security/shared/types/tenant/index";
import {
  Col,
  Collapse,
  Flex,
  Modal,
  Row,
  Select,
  Spacing,
  Spin,
  Tag,
  Typography,
  message,
} from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import { IdpGroupPreview } from "components/Jit/Policy/components/previews/IdpGroupPreview";
import { useFlow } from "components/Welcome/hooks/useFlow";
import { doc, getDoc } from "firebase/firestore";
import { validate as validateEmail } from "isemail";
import { jwtDecode } from "jwt-decode";
import { useFlags } from "launchdarkly-react-client-sdk";
import { compact, uniqBy } from "lodash";
import { ResourcesProvider } from "providers/ResourcesProvider";
import React, {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { useInstalledDirectories } from "store/resources/DirectoriesProvider";
import { handleErrorWithToastMessage } from "utils/error";
import { z } from "zod";

import { TagInput } from "../TagInput";
import { ApiKeysPanel } from "./ApiKeysPanel";
import { ApprovalRequirements } from "./ApprovalRequirements";

const { Title } = Typography;

const REMOVE_OWN_ACCESS_ERROR =
  "You can't remove yourself as an owner. Instead, add another member of your organization, then have them remove you.";

/**
 * Schema for the Firebase token payload
 */
const firebaseTokenSchema = z.object({
  firebase: z.object({
    sign_in_attributes: z
      .object({
        groups: z.array(z.string()).optional(),
      })
      .optional(),
  }),
});

interface EmailDiff {
  added: string[];
  removed: string[];
  warnings: {
    outOfDomain: string[];
    invalidEmails: string[];
    count: number;
    multi: boolean;
  };
}

const emailDiff = (
  oldEmails: string[],
  newEmails: string[],
  emailRegex: RegExp
): EmailDiff => {
  const old = new Set(oldEmails);
  const new_ = new Set(newEmails);

  const added = Array.from(new_.difference(old).values());
  const removed = Array.from(old.difference(new_).values());

  const invalidEmails = added.filter((email) => !validateEmail(email));
  // Avoid double-warning on emails that are already an `invalidEmail`
  const outOfDomain = added.filter(
    (email) => validateEmail(email) && !emailRegex.test(email)
  );

  return {
    added,
    removed,
    warnings: {
      outOfDomain,
      invalidEmails,
      get count(): number {
        return this.outOfDomain.length + this.invalidEmails.length;
      },
      get multi(): boolean {
        return (
          [this.outOfDomain.length, this.invalidEmails.length].reduce(
            (prev, cur) => prev + (cur > 0 ? 1 : 0),
            0
          ) > 1
        );
      },
    },
  };
};

const DEFAULT_TITLE = "Email Confirmation";

const orgDomainMessage = (arr: string[]): string =>
  arr.length > 1
    ? "These emails do not match your organization's domain."
    : "This email does not match your organization's domain.";

const invalidEmailMessage = (arr: string[]): string =>
  arr.length > 1 ? "These emails are not valid." : "This email is not valid.";

const inferTitle = (diff?: EmailDiff | null): string => {
  if (!diff || !diff.warnings.count || diff.warnings.multi) {
    return DEFAULT_TITLE;
  }

  const { outOfDomain, invalidEmails } = diff.warnings;
  return outOfDomain.length
    ? orgDomainMessage(outOfDomain)
    : invalidEmails.length
      ? invalidEmailMessage(invalidEmails)
      : DEFAULT_TITLE;
};

const MemberEditConfirmModal: React.FC<
  ComponentProps<typeof Modal> & {
    stagedDiff?: EmailDiff;
  }
> = ({ stagedDiff, ...rest }) => {
  const { warnings } = stagedDiff ?? {};

  return (
    <Modal
      title={inferTitle(stagedDiff)}
      closable={{ "aria-label": "Custom Close Button" }}
      open={!!stagedDiff}
      okText={
        (stagedDiff?.warnings.count ?? 1) > 1
          ? "Yes, Add Emails"
          : "Yes, Add Email"
      }
      {...rest}
    >
      {warnings === undefined ? (
        "Something is wrong, are you sure you want to add these emails?"
      ) : (
        <>
          <p>
            {warnings.count > 1
              ? "Are you sure you want to add them?"
              : "Are you sure you want to add it?"}
          </p>
          {warnings.outOfDomain.length > 0 && (
            <>
              {warnings.multi && (
                <p>{orgDomainMessage(warnings.outOfDomain)}</p>
              )}
              <Flex gap="small" wrap>
                {warnings.outOfDomain.map((m) => (
                  <Tag key={m}>{m}</Tag>
                ))}
              </Flex>
            </>
          )}

          {warnings.invalidEmails.length > 0 && (
            <>
              {warnings.multi && (
                <p>{invalidEmailMessage(warnings.invalidEmails)}</p>
              )}

              <Flex gap="small" wrap>
                {warnings.invalidEmails.map((m) => (
                  <Tag key={m}>{m}</Tag>
                ))}
              </Flex>
            </>
          )}
        </>
      )}
    </Modal>
  );
};

const MembersEdit: React.FC<{ role: UserRole }> = ({ role }) => {
  const tenantId = useContext(Tenant);
  const { user } = useUser();
  const authFetch = useAuthFetch(handleErrorWithToastMessage);
  const derivedRole = useMemo(
    () => role.replace("approver", "manager"),
    [role]
  );
  const tenantPath = useMemo(() => `o/${tenantId}`, [tenantId]);
  const { doc: tenantDoc } = useFirestoreDoc<
    { openIdDomain: string } | undefined
  >(tenantPath, {
    live: true,
  });
  const openIdDomain = tenantDoc?.data?.openIdDomain;

  const path = useMemo(
    () => `o/${tenantId}/roles/${derivedRole}/bindings`,
    [derivedRole, tenantId]
  );
  const { docs: membersDocs } = useFirestoreCollection<{ email: string }>(
    path,
    {
      live: true,
    }
  );
  const members = useMemo(
    () => membersDocs?.map((m) => m.data.email ?? m.id) ?? [],
    [membersDocs]
  );

  const updateMembership = useCallback(
    (method: "DELETE" | "PUT", email: string) =>
      authFetch(
        `settings/roles/${derivedRole}/bindings/users/${encodeURIComponent(
          email.toLowerCase()
        )}`,
        { method }
      ),
    [authFetch, derivedRole]
  );

  const applyDiff = useCallback(
    async ({ added, removed }: Pick<EmailDiff, "added" | "removed">) => {
      for (const email of added) {
        const response = await updateMembership("PUT", email);
        if (response) message.success(`Added ${email}`);
      }
      for (const email of removed) {
        const response = await updateMembership("DELETE", email);
        if (response) message.success(`Removed ${email}`);
      }
    },
    [updateMembership]
  );

  const [stagedDiff, setStagedDiff] = useState<EmailDiff | undefined>(
    undefined
  );

  const onChange = useCallback(
    async (newMembers: string[]) => {
      const diff = emailDiff(
        members,
        newMembers,
        new RegExp(openIdDomain ?? ".*")
      );

      if (!diff.warnings.count) {
        await applyDiff(diff);
        return;
      }

      setStagedDiff(diff);
    },
    [members, applyDiff, openIdDomain, setStagedDiff]
  );

  const onModalOk = useCallback(async () => {
    if (stagedDiff) await applyDiff(stagedDiff);
    setStagedDiff(undefined);
  }, [stagedDiff, applyDiff, setStagedDiff]);

  const onModalCancel = useCallback(() => {
    setStagedDiff(undefined);
  }, [setStagedDiff]);

  return openIdDomain === undefined || membersDocs === undefined ? (
    <Spin />
  ) : (
    <>
      <MemberEditConfirmModal
        onOk={onModalOk}
        onCancel={onModalCancel}
        stagedDiff={stagedDiff}
      />
      <Flex align="baseline" gap="0.5em">
        <div>Users:</div>
        <SettingsTagInput
          user={user}
          role={role}
          tags={members}
          onChange={onChange}
        />
      </Flex>
    </>
  );
};

const SettingsTagInput: React.FC<{
  tags: string[];
  role: UserRole;
  user?: NormalizedUser;
  onChange: (tags: string[]) => void;
}> = ({ tags, role, user, onChange }) => {
  const tagToElement = useCallback(
    (tag: string, onClose: () => void) => {
      const canClose = role !== "owner" || tag !== user?.email;
      const key = tag;
      const tagElem = (
        <Tag key={key} closable={canClose} onClose={onClose}>
          {tag}
        </Tag>
      );
      return canClose ? (
        tagElem
      ) : (
        <GraphTooltip key={key} title={REMOVE_OWN_ACCESS_ERROR}>
          {tagElem}
        </GraphTooltip>
      );
    },
    [role, user]
  );

  return (
    <Spacing asChild mt="0.5em">
      <TagInput
        placeholder="Enter an email address"
        tags={tags}
        // Superset pattern from https://stackoverflow.com/questions/201323/how-can-i-validate-an-email-address-using-a-regular-expression
        tagPattern={/[a-zA-Z0-9:.!#$%&'*+/=?^_`{|}~\-@]/}
        tagToElement={tagToElement}
        onUpdate={onChange}
        key={role}
      />
    </Spacing>
  );
};

/** The value a sign-in token's groups claim carries for a directory group.
 * Okta sends group names; Entra sends object IDs. */
const groupClaimValue = (group: IdpGroup) =>
  group.directory === "okta" ? group.label : group.id;

/** Directories whose groups a sign-in token's groups claim can name.
 * Google sign-in sends no groups claim, so no directory applies to it. */
const claimDirectories = (auth: OrgAuth | undefined): Directory[] => {
  if (auth?.type !== "sso") return [];
  const { provider } = auth;
  switch (provider.ssoProvider) {
    case "azure-oidc":
    case "microsoft":
      return ["entra-id", "azure-ad"];
    case "okta":
      return ["okta"];
    case "oidc-pkce":
      return provider.providerType === "okta" ? ["okta"] : [];
    case "google":
    case "google-oidc":
      return [];
    default:
      throw assertNever(provider);
  }
};

/** Edits groups that can access a role */
const GroupsEdit: React.FC<{ role: UserRole }> = ({ role }) => {
  const tenantId = useContext(Tenant);
  const { user, orgData } = useUser();
  const authFetch = useAuthFetch(handleErrorWithToastMessage);
  const derivedRole = useMemo(
    () => role.replace("approver", "manager"),
    [role]
  );
  const path = useMemo(
    () => `o/${tenantId}/roles/${derivedRole}`,
    [derivedRole, tenantId]
  );
  const roleData = useFirestoreDoc<{ allowed?: { groups: string[] } }>(path, {
    live: true,
  });
  const groups = useMemo(
    () => roleData.doc?.data.allowed?.groups ?? [],
    [roleData]
  );

  const { allAvailableGroups, availableDirectories, isLoadingGroups } =
    useInstalledDirectories();
  const directories = useMemo(() => claimDirectories(orgData?.auth), [orgData]);
  const pickableGroups = useMemo(() => {
    const groups = allAvailableGroups.filter(
      (group) =>
        directories.includes(group.directory) &&
        availableDirectories.directories.includes(group.directory)
    );
    // Both Entra integrations list the same groups under the same object IDs
    return uniqBy(groups, groupClaimValue);
  }, [allAvailableGroups, availableDirectories, directories]);
  const groupOptions = useMemo(
    () =>
      pickableGroups.map((group) => ({
        label: <IdpGroupPreview idpGroup={group} showPopover={false} />,
        value: groupClaimValue(group),
        search: group.label,
      })),
    [pickableGroups]
  );
  // Keep free text until there are groups to pick, so a failed or empty fetch never blocks entry
  const canPick = groupOptions.length > 0;
  const groupNames = useMemo(
    () =>
      Object.fromEntries(
        pickableGroups.map((group) => [groupClaimValue(group), group.label])
      ),
    [pickableGroups]
  );

  const [userGroups, setUserGroups] = useState<string[] | undefined>(undefined);

  useEffect(() => {
    const fetchUserGroups = async () => {
      const token = await getCurrentToken();
      if (!token) return;
      const userJwtData = firebaseTokenSchema.parse(jwtDecode<unknown>(token));
      setUserGroups(userJwtData.firebase.sign_in_attributes?.groups);
    };
    fetchUserGroups();
  }, [user]);

  const updateGroupMembership = useCallback(
    (method: "DELETE" | "PUT", group: string) =>
      authFetch(
        `settings/roles/${derivedRole}/bindings/groups/${encodeURIComponent(
          group
        )}`,
        { method }
      ),
    [authFetch, derivedRole]
  );

  const saveGroups = useCallback(
    async (newGroups: string[]) => {
      if (!user || !userGroups) return;
      if (derivedRole === "owner") {
        // Prevent owners from removing the last binding that grants them ownership
        const userIsInGroups = newGroups.find((g) => userGroups?.includes(g));
        if (!userIsInGroups) {
          const userBindingExists = (
            await getDoc(doc(DB, `${path}/bindings/${user.email}`))
          ).exists();
          if (!userBindingExists) {
            // TODO: Prevent closing of last group tag
            message.error(REMOVE_OWN_ACCESS_ERROR);
            return;
          }
        }
      }

      const names = newGroups.map((g) => groupNames[g] ?? g);
      const addedGroups = newGroups.filter((g) => !groups.includes(g));
      const removedGroups = groups.filter((g) => !newGroups.includes(g));

      for (const addedGroup of addedGroups) {
        const response = await updateGroupMembership("PUT", addedGroup);
        if (response) message.success(`Groups ${names} added`);
      }
      for (const removedGroup of removedGroups) {
        const response = await updateGroupMembership("DELETE", removedGroup);
        if (response) message.success(`Groups ${names} removed`);
      }
    },
    [
      derivedRole,
      groupNames,
      groups,
      path,
      updateGroupMembership,
      user,
      userGroups,
    ]
  );

  // If user does not have a groups claim, this provider is not compatible with group access
  return !userGroups ? null : roleData === undefined ||
    availableDirectories.state === "loading" ? (
    <Spin />
  ) : (
    <Flex align="baseline" gap="0.5em">
      <div>Groups:</div>
      <Spacing asChild mt="0.5em">
        {canPick ? (
          <Select
            mode="multiple"
            options={groupOptions}
            optionFilterProp="search"
            value={groups}
            onChange={saveGroups}
            loading={isLoadingGroups}
            placeholder="Select groups"
            className="flex-1"
            key={role}
          />
        ) : (
          <TagInput
            placeholder="Enter a group name"
            tags={groups}
            tagPattern={/[a-zA-Z0-9-_ ]/}
            onUpdate={saveGroups}
            key={role}
          />
        )}
      </Spacing>
    </Flex>
  );
};

export const Admin: React.FC = () => {
  const [flow] = useFlow();

  const flags = useFlags();
  return (
    <ResourcesProvider>
      <Collapse
        defaultActiveKey={["access_control", "jit", "apikeys"]}
        items={compact([
          {
            key: "access_control",
            label: "Access control",
            children: (
              <Row gutter={[0, 32]}>
                <Col span={24}>
                  <Title level={4}>Owners</Title>
                  Owners can add integrations and alter settings.
                  <MembersEdit role="owner" />
                  <GroupsEdit role="owner" />
                </Col>
                {/* Show all roles if now flow is passed */}
                {/* If one is passed, only show relevant roles */}
                {flow !== "assessment" && (
                  <Col span={24}>
                    <Title level={4}>Security Reviewers</Title>
                    Security Reviewers can review policies and user access (via
                    P0 CLI), and optionally be configured as approvers for
                    access requests in {/* nosemgrep */}
                    <Link
                      to={
                        flow
                          ? DOCS_URLS.JIT_POLICIES
                          : `../${AppPaths.Policies}`
                      }
                      target={flow ? "_blank" : undefined}
                      rel={flow ? "noopener noreferrer" : undefined}
                    >
                      Policies
                    </Link>
                    .
                    <MembersEdit role="approver" />
                    <GroupsEdit role="approver" />
                  </Col>
                )}
                {flow !== "jit" && (
                  <Col span={24}>
                    <Title level={4}>Assessment Users</Title>
                    Assessment Users can run, manage, and view IAM assessments.
                    <MembersEdit role="iamOwner" />
                    <GroupsEdit role="iamOwner" />
                  </Col>
                )}
                {flow !== "jit" && (
                  <Col span={24}>
                    <Title level={4}>Assessment Viewers</Title>
                    Assessment Viewers can view IAM assessments.
                    <MembersEdit role="iamViewer" />
                    <GroupsEdit role="iamViewer" />
                  </Col>
                )}

                {
                  /* This role is not used for either onboarding flow.
                  Currently only for Splunk while we are working with CX to evaluate the install validation
                  experience for other customers */
                  !flow && flags.splunkInstallEmails && (
                    <Col span={24}>
                      <Title level={4}>Account Contacts</Title>
                      Account Contacts receive email notifications for this
                      account, such as invalid installation warnings.
                      <MembersEdit role="contact" />
                    </Col>
                  )
                }
              </Row>
            ),
          },
          flow
            ? undefined
            : {
                key: "access_management",
                label: "Access management",
                children: <ApprovalRequirements />,
              },
          flow
            ? undefined
            : { key: "apikeys", label: "API keys", children: <ApiKeysPanel /> },
        ])}
      />
    </ResourcesProvider>
  );
};
